import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, CheckCircle, Clock, AlertTriangle, RefreshCw,
  Plus, Settings as SettingsIcon, Users, Award, Edit2, Trash2, Info
} from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';
import { LmsMappingModal } from './LmsMappingModal';
import { getLmsAdapter, type MappingRow } from '../lib/lms-adapters';
import { useConfirm } from './ui';
import { getLmsErrorInfo, inferLmsErrorCode } from '../utils/lmsErrorMessages';

type LmsType = 'moodle' | 'tutorlms' | 'learndash' | 'xtramile';

interface Mapping {
  mapping_id: string;
  lms_course_id: string;
  lms_course_title: string;
  lms_course_subtitle: string | null;
  study_plan_id: string | null;
  study_plan_title: string | null;
  study_plan_status: string | null;
  mapping_enabled: boolean;
  course_exists: boolean;
  assessment_type: string | null;
  ects_credits: number | null;
  mapping_created_at: string;
  mapping_updated_at: string;
}

interface Integration {
  lms_type: LmsType;
  display_name: string;
  integration_id: string;
  member_id: string;
  member_name: string;
  forbund_name: string;
  organization_id: string;
  status: {
    last_sync_at: string | null;
    last_sync_status: string | null;
    last_sync_error: string | null;
    sync_enabled: boolean;
    sync_interval_minutes: number;
    approved_at: string;
    next_sync_estimate: string | null;
  };
  stats: {
    total_synced_completions: number;
    total_certificates_issued: number;
    recent_completions_30d: number;
    mappings_count: number;
    active_mappings_count: number;
  };
  mappings: Mapping[];
}

interface OverviewData {
  integrations: Integration[];
  stats: {
    integrations_count: number;
    total_mappings: number;
    total_active_mappings: number;
    total_completions_30d: number;
    total_certificates_issued: number;
  };
}

const LMS_COLORS: Record<LmsType, { bg: string; border: string; text: string; badge: string }> = {
  moodle: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', badge: 'bg-orange-100 text-orange-800' },
  tutorlms: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', badge: 'bg-purple-100 text-purple-800' },
  learndash: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', badge: 'bg-green-100 text-green-800' },
  xtramile: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-800' }
};

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'aldri';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'akkurat nå';
  if (min < 60) return `for ${min} min siden`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `for ${hours}t siden`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `for ${days}d siden`;
  return new Date(iso).toLocaleDateString('nb-NO');
}

interface AwaitingDataRow {
  integration_type: LmsType;
  integration_id: string;
  mapping_id: string;
  lms_course_id: string;
  lms_course_name: string | null;
  candidate_name: string | null;
  candidate_email: string | null;
  missing_data: Array<{ code: string; label: string; recommendation: string }>;
  completion_detected_at: string;
  arranger_notified_at: string | null;
  member_name: string | null;
}

export default function LmsCoursesPage() {
  const { confirm } = useConfirm();
  const [data, setData] = useState<OverviewData | null>(null);
  const [awaiting, setAwaiting] = useState<AwaitingDataRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<LmsType | 'all'>('all');
  // Mapping-modal state
  const [modalState, setModalState] = useState<{
    lmsType: LmsType;
    memberId: string;
    existing: MappingRow | null;
  } | null>(null);

  useEffect(() => { loadOverview(); }, []);

  const loadOverview = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch('/api/lms-courses/overview');
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Kunne ikke hente oversikt');
      setData(json);
      // Hent også fullføringer som venter på data (parallelt — feiler stille)
      try {
        const r2 = await authenticatedFetch('/api/lms-courses/awaiting-data');
        if (r2.ok) {
          const j2 = await r2.json();
          setAwaiting(j2.awaiting || []);
        }
      } catch (_) { /* ignorer — varslingen er ikke kritisk */ }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
        Laster LMS-oversikt...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded">
        <p className="text-red-700">Feil: {error}</p>
      </div>
    );
  }

  if (!data || data.integrations.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Ingen aktive LMS-integrasjoner</h2>
        <p className="text-gray-600 mb-4">
          Du har ingen godkjente LMS-integrasjoner ennå. Gå til innstillinger for å sette opp en
          tilkobling til f.eks. Moodle, TutorLMS eller LearnDash.
        </p>
        <Link
          to="/member-settings/integrations"
          className="inline-flex items-center gap-2 px-4 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary/90"
        >
          <SettingsIcon className="h-4 w-4" />
          Gå til integrasjonsinnstillinger
        </Link>
      </div>
    );
  }

  const filteredIntegrations = activeFilter === 'all'
    ? data.integrations
    : data.integrations.filter(i => i.lms_type === activeFilter);

  const availableTypes = Array.from(new Set(data.integrations.map(i => i.lms_type))) as LmsType[];

  return (
    <div className="space-y-6">
      {/* Header stats */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">LMS-kurs</h1>
            <p className="text-gray-600 mt-1">
              Operativ oversikt over koblinger mellom kurs i eksterne LMS og studieplaner
            </p>
          </div>
          <button
            onClick={loadOverview}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Oppdater
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Aktive integrasjoner</p>
            <p className="text-2xl font-bold text-gray-900">{data.stats.integrations_count}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Aktive koblinger</p>
            <p className="text-2xl font-bold text-gray-900">
              {data.stats.total_active_mappings}
              <span className="text-sm font-normal text-gray-500 ml-1">/ {data.stats.total_mappings}</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Fullføringer siste 30d</p>
            <p className="text-2xl font-bold text-gray-900">{data.stats.total_completions_30d}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Attester totalt</p>
            <p className="text-2xl font-bold text-gray-900">{data.stats.total_certificates_issued}</p>
          </div>
        </div>
      </div>

      {/* Filter tabs (only if more than one LMS type) */}
      {availableTypes.length > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 text-sm rounded-full border transition ${
              activeFilter === 'all'
                ? 'bg-kf-primary text-white border-kf-primary'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Alle ({data.integrations.length})
          </button>
          {availableTypes.map(type => {
            const count = data.integrations.filter(i => i.lms_type === type).length;
            return (
              <button
                key={type}
                onClick={() => setActiveFilter(type)}
                className={`px-4 py-2 text-sm rounded-full border transition whitespace-nowrap ${
                  activeFilter === type
                    ? `${LMS_COLORS[type].badge} border-transparent font-medium`
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {data.integrations.find(i => i.lms_type === type)?.display_name} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Integration sections */}
      <div className="space-y-6">
        {filteredIntegrations.map(integration => (
          <IntegrationSection
            key={integration.integration_id}
            integration={integration}
            awaiting={awaiting.filter(a => a.integration_id === integration.integration_id)}
            onAddMapping={() => setModalState({
              lmsType: integration.lms_type,
              memberId: integration.member_id,
              existing: null
            })}
            onEditMapping={(mapping) => setModalState({
              lmsType: integration.lms_type,
              memberId: integration.member_id,
              existing: mapping
            })}
            onDeleteMapping={async (mapping) => {
              const adapter = getLmsAdapter(integration.lms_type);
              if (!adapter) return;
              const ok = await confirm({
                title: 'Slett kurskobling',
                message: `Vil du virkelig slette koblingen mellom "${mapping.lms_course_name}" og "${mapping.study_plan_title}"? Kandidater som allerede har attest beholder den.`
              });
              if (!ok) return;
              try {
                const res = await authenticatedFetch(adapter.endpoints.deleteMapping(mapping.mapping_id), { method: 'DELETE' });
                if (!res.ok) throw new Error('Sletting feilet');
                loadOverview();
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          />
        ))}
      </div>

      {/* Mapping-modal */}
      {modalState && (() => {
        const adapter = getLmsAdapter(modalState.lmsType);
        if (!adapter) return null;
        return (
          <LmsMappingModal
            adapter={adapter}
            memberId={modalState.memberId}
            existing={modalState.existing}
            onClose={() => setModalState(null)}
            onSaved={() => { setModalState(null); loadOverview(); }}
          />
        );
      })()}
    </div>
  );
}

interface IntegrationSectionProps {
  integration: Integration;
  awaiting: AwaitingDataRow[];
  onAddMapping: () => void;
  onEditMapping: (mapping: MappingRow) => void;
  onDeleteMapping: (mapping: MappingRow) => void;
}

function IntegrationSection({ integration, awaiting, onAddMapping, onEditMapping, onDeleteMapping }: IntegrationSectionProps) {
  const colors = LMS_COLORS[integration.lms_type];
  const status = integration.status;

  const syncStatusLabel =
    !status.sync_enabled ? 'Synk pauset' :
    status.last_sync_status === 'error' ? 'Feil ved siste synk' :
    status.last_sync_status === 'running' ? 'Synker nå' :
    'Aktiv';

  const syncStatusIcon =
    !status.sync_enabled ? Clock :
    status.last_sync_status === 'error' ? AlertTriangle :
    CheckCircle;

  const SyncIcon = syncStatusIcon;

  // Settings-lenken peker kun til integrasjon-oppsett (API-nøkkel, samtykke).
  // Koblinger administreres på denne siden, ikke i settings.
  const settingsLink = `/member-settings/integrations?lms=${integration.lms_type}`;

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${colors.border}`}>
      {/* Integration header */}
      <div className={`${colors.bg} ${colors.border} border-b px-6 py-4`}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded ${colors.badge}`}>
              <BookOpen className="h-5 w-5" />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${colors.text}`}>
                {integration.display_name}
              </h2>
              <p className="text-sm text-gray-600">{integration.member_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1.5">
              <SyncIcon className={`h-4 w-4 ${
                status.last_sync_status === 'error' ? 'text-red-600' :
                !status.sync_enabled ? 'text-gray-500' :
                'text-green-600'
              }`} />
              <span className="font-medium">{syncStatusLabel}</span>
            </div>
            <span className="text-gray-400">·</span>
            <span className="text-gray-600">Siste synk: {formatRelativeTime(status.last_sync_at)}</span>
            <Link
              to={settingsLink}
              className="ml-2 text-gray-500 hover:text-gray-800"
              title="Åpne innstillinger"
            >
              <SettingsIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {status.last_sync_error && (() => {
          const code = inferLmsErrorCode(status.last_sync_error);
          const info = getLmsErrorInfo(code, status.last_sync_error);
          if (info.code === 'unknown') {
            return (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <strong>Feil:</strong> {status.last_sync_error}
              </div>
            );
          }
          return (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900 text-sm">{info.title}</p>
                  <p className="text-sm text-red-800 mt-1">{info.description}</p>
                </div>
              </div>
              <div className="ml-6 bg-white border border-red-200 rounded p-2.5">
                <p className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Hva du kan gjøre:
                </p>
                <p className="text-sm text-gray-800 whitespace-pre-line">{info.guidance}</p>
                <p className="text-[11px] text-gray-500 mt-2 font-mono">Feilkode: {info.code}</p>
              </div>
            </div>
          );
        })()}

        <div className="mt-3 flex items-center gap-4 text-xs text-gray-600 flex-wrap">
          <span><Users className="h-3.5 w-3.5 inline mr-1" />{integration.stats.recent_completions_30d} fullføringer siste 30d</span>
          <span><Award className="h-3.5 w-3.5 inline mr-1" />{integration.stats.total_certificates_issued} attester utstedt totalt</span>
          {status.next_sync_estimate && status.sync_enabled && (
            <span><Clock className="h-3.5 w-3.5 inline mr-1" />Neste synk: {new Date(status.next_sync_estimate).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </div>
      </div>

      {/* Mappings */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            Kurskoblinger ({integration.stats.active_mappings_count} aktive)
          </h3>
          <button
            onClick={onAddMapping}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded ${colors.badge} hover:opacity-80`}
          >
            <Plus className="h-4 w-4" />
            Legg til kobling
          </button>
        </div>

        {integration.mappings.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center border-2 border-dashed rounded">
            Ingen koblinger ennå. Klikk "Legg til kobling" for å koble et {integration.display_name}-kurs til en studieplan.
          </div>
        ) : (
          <div className="space-y-1">
            {integration.mappings.map(m => {
              const awaitingForThis = awaiting.filter(a => a.mapping_id === m.mapping_id);
              return (
              <div key={m.mapping_id}>
              {awaitingForThis.length > 0 && (
                <div className="mb-2 ml-4 p-3 bg-amber-50 border border-amber-300 rounded">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-amber-900 text-sm">
                        {awaitingForThis.length} {awaitingForThis.length === 1 ? 'kandidat har' : 'kandidater har'} fullført kurset, men attest kan ikke utstedes
                      </p>
                      <p className="text-xs text-amber-800 mt-1">
                        Følgende mangler i LMS-systemet og må rettes der. Etter retting utsteder neste sync attesten automatisk og kandidaten varsles på SMS og e-post.
                      </p>
                      <ul className="mt-2 space-y-2">
                        {awaitingForThis.map((a, idx) => (
                          <li key={idx} className="bg-white border border-amber-200 rounded p-2.5 text-xs">
                            <div className="font-medium text-gray-900">
                              {a.candidate_name || '(navn mangler)'}{a.candidate_email && ` · ${a.candidate_email}`}
                            </div>
                            <div className="text-gray-500 text-[11px] mb-1">
                              Fullført {new Date(a.completion_detected_at).toLocaleDateString('nb-NO')}
                            </div>
                            <ul className="space-y-1 list-disc list-inside ml-1 text-gray-700">
                              {a.missing_data?.map((md, mdIdx) => (
                                <li key={mdIdx}>
                                  <span className="font-medium">Mangler {md.label.toLowerCase()}:</span> {md.recommendation}
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              <MappingRow
                mapping={m}
                lmsType={integration.lms_type}
                onEdit={() => onEditMapping({
                  mapping_id: m.mapping_id,
                  lms_course_id: m.lms_course_id,
                  lms_course_name: m.lms_course_title,
                  study_plan_id: m.study_plan_id || '',
                  study_plan_title: m.study_plan_title || '',
                  validation_status: '',
                  module_codes: null,
                  assessment_type: m.assessment_type || 'external_sensor',
                  is_active: m.mapping_enabled,
                  created_at: m.mapping_created_at
                })}
                onDelete={() => onDeleteMapping({
                  mapping_id: m.mapping_id,
                  lms_course_id: m.lms_course_id,
                  lms_course_name: m.lms_course_title,
                  study_plan_id: m.study_plan_id || '',
                  study_plan_title: m.study_plan_title || '',
                  validation_status: '',
                  module_codes: null,
                  assessment_type: m.assessment_type || '',
                  is_active: m.mapping_enabled,
                  created_at: m.mapping_created_at
                })}
              />
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MappingRow({ mapping, lmsType: _lmsType, onEdit, onDelete }: {
  mapping: Mapping;
  lmsType: LmsType;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const isLinked = !!mapping.study_plan_id;

  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded hover:bg-gray-50 ${!mapping.mapping_enabled ? 'opacity-60' : ''}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 truncate">
            {mapping.lms_course_title || `Kurs #${mapping.lms_course_id}`}
          </span>
          {!mapping.course_exists && (
            <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title="Kurs ikke funnet i LMS">
              Mangler i LMS
            </span>
          )}
          {!mapping.mapping_enabled && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">
              Deaktivert
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 mt-0.5">
          <span className="text-gray-400">→</span>
          {isLinked ? (
            <Link to={`/study-plans/${mapping.study_plan_id}`} className="text-kf-primary hover:underline truncate">
              {mapping.study_plan_title || 'Studieplan'}
            </Link>
          ) : (
            <span className="text-amber-600">Ikke koblet til studieplan</span>
          )}
          {mapping.ects_credits != null && (
            <span className="text-xs text-gray-500">· {mapping.ects_credits} stp</span>
          )}
          {mapping.assessment_type && mapping.assessment_type !== 'unverified' && (
            <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">{mapping.assessment_type}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <button
          onClick={onEdit}
          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
          title="Rediger kobling"
        >
          <Edit2 className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-gray-400 hover:text-red-700 hover:bg-red-50 rounded"
          title="Slett kobling"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
